/**
 * @file index.tsx
 * @module app/modules/customers
 * @description Defines customer-management routes, page metadata, and role-specific customer-list views.
 * Includes customer creation, profiles, payments, reports, ledgers, refunds, and pre-order customers.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { PageLink, PageTitle } from "../../../_admin/layout/core";
import AddNewCustomers from "./add-new-customers";
import ViewCustomers from "./view-customers";
import CustomerProfile from "./customer-profile";
import ViewCustomersManager from "./view-customers-manager";
import ViewCustomersMember from "./view-customers-member";
import { useAuth } from "../auth";
import {
  FinanceManager,
  FulfillmentManager,
  ServiceMember,
} from "../../../utils/constants";
import RefundsToCustomers from "../finance/refunds/refunds-to-customers";
import CustomerFinanceDetails from "../finance/refunds/customer-finance-details";
import MakeRefund from "../finance/refunds/make-refund";
import ReceivableReport from "./receivable-report";
import CollectPayment from "./collect-payment";
import PreOrderCustomers from "./pre-order-customers";
import CustomerLedgerPage from "./customer-ledger";
import DriverLedgerPage from "./driver-ledger";
const customerBreadCrumbs: Array<PageLink> = [
  {
    title: "Customers",
    path: "/customers",
    isSeparator: false,
    isActive: false,
  },
  {
    title: "",
    path: "",
    isSeparator: true,
    isActive: false,
  },
];
const Index = () => {
  const { currentUser } = useAuth();
  return (
    <Routes>
      <Route element={<Outlet />}>
        <Route path="*" element={<Navigate to="/error/404" />} />
        <Route
          index
          element={
            <>
              <PageTitle>
                Customers
              </PageTitle>
              {currentUser?.role.length ? (
                currentUser?.role.some(
                  (obj: any) => obj.type === ServiceMember
                ) ? (
                  <ViewCustomersMember />
                ) : currentUser?.role.some(
                    (obj: any) =>
                      obj.type === FinanceManager ||
                      obj.type === FulfillmentManager
                  ) ? (
                  <ViewCustomers />
                ) : (
                  <ViewCustomersManager />
                )
              ) : (
                <ViewCustomers />
              )}
            </>
          }
        />
        <Route
          path="add-customers"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Add Customers
              </PageTitle>
              <AddNewCustomers />
            </>
          }
        />
        <Route
          path="add-new-customers"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Add New Customers
              </PageTitle>
              <AddNewCustomers />
            </>
          }
        />
        <Route
          path="refunds-to-customers"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Refunds To Customers
              </PageTitle>
              <RefundsToCustomers />
            </>
          }
        />
        <Route
          path="receivable-report"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Receivable Report
              </PageTitle>
              <ReceivableReport />
            </>
          }
        />
        <Route
          path="receivable-report/collect-payment"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Collect Payment
              </PageTitle>
              <CollectPayment />
            </>
          }
        />
        <Route
          path="customer-ledger"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Customer Ledger
              </PageTitle>
              <CustomerLedgerPage />
            </>
          }
        />
        <Route
          path="driver-ledger"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Driver Ledger
              </PageTitle>
              <DriverLedgerPage />
            </>
          }
        />
        <Route
          path="refunds-to-customers/customer-refund-details"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Customer Refund Details
              </PageTitle>
              <CustomerFinanceDetails />
            </>
          }
        />
        <Route
          path="refunds-to-customers/make-refund"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Make Refund
              </PageTitle>
              <MakeRefund />
            </>
          }
        />
        <Route
          path="add-customers/add-new-customers/view-customers"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Customers
              </PageTitle>
              <ViewCustomers />
            </>
          }
        />
        <Route
          path="pre-order-customers-list"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Pre-order customers list
              </PageTitle>
              <PreOrderCustomers />
            </>
          }
        />
        <Route
          path="view-customers"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Customers
              </PageTitle>
              {currentUser?.role.length ? (
                currentUser?.role.some(
                  (obj: any) => obj.type === ServiceMember
                ) ? (
                  <ViewCustomersMember />
                ) : currentUser?.role.some(
                    (obj: any) =>
                      obj.type === FinanceManager ||
                      obj.type === FulfillmentManager
                  ) ? (
                  <ViewCustomers />
                ) : (
                  <ViewCustomersManager />
                )
              ) : (
                <ViewCustomers />
              )}
            </>
          }
        />
        <Route
          path="customer-profile"
          element={
            <>
              <PageTitle breadcrumbs={customerBreadCrumbs}>
                Customer Profile
              </PageTitle>
              <CustomerProfile />
            </>
          }
        />
      </Route>
    </Routes>
  );
};
export default Index;
